/**
 * Phase 8 T-12 §12 A–C — Live interview layout (Mobbin 140.png).
 *
 * Recruiter-facing live view. Full-viewport video canvas on the left
 * (75% on lg+), 320px right rail (Details · Candidate tabs), and a
 * 56px bottom action strip with a timer pill, media controls, and
 * a red `Leave` button.
 *
 * Visual shell only — live-streaming wiring is intentionally not
 * touched (see T-12 DON'Ts). The video canvas renders whatever
 * children the parent mounts, or a placeholder when nothing is wired.
 */

import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  Circle,
  Copy,
  LogOut,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  MoreHorizontal,
  Square,
  User as UserIcon,
  Video,
  VideoOff,
} from 'lucide-react';
import type { Candidate } from '@core/types';

type LiveTab = 'details' | 'candidate';

export interface InterviewLiveAttendee {
  id: string;
  name: string;
  email?: string | null;
  role?: 'host' | 'panelist' | 'candidate' | 'guest';
  muted?: boolean;
  videoOn?: boolean;
}

export interface InterviewLiveLayoutProps {
  /** Meeting title displayed top-left inside the video canvas. */
  meetingTitle: string;
  /** Full meeting URL; shown with a copy icon in the Details tab. */
  meetingUrl: string;
  companyName: string;
  candidate: Candidate | null;
  /** Company-side attendees (panelists, host). */
  companyAttendees: InterviewLiveAttendee[];
  /** Seconds since the meeting started — rendered as `mm:ss` in the timer pill. */
  elapsedSeconds?: number;
  recording?: boolean;
  muted?: boolean;
  videoOn?: boolean;
  presenting?: boolean;
  onToggleRecording?: () => void;
  onToggleMute?: () => void;
  onToggleVideo?: () => void;
  onTogglePresent?: () => void;
  onMenu?: () => void;
  onLeave: () => void;
  /** Render inside the video canvas; fall back to a placeholder if null. */
  canvas?: ReactNode;
  /** Anchor under the breadcrumb when no direct `canvas` node is provided. */
  placeholderLabel?: string;
}

function formatElapsed(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${String(m).padStart(2, '0')}:${String(rem).padStart(2, '0')}`;
}

function initials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function InterviewLiveLayout(props: InterviewLiveLayoutProps) {
  const {
    meetingTitle,
    meetingUrl,
    companyName,
    candidate,
    companyAttendees,
    elapsedSeconds = 0,
    recording = false,
    muted = false,
    videoOn = true,
    presenting = false,
    onToggleRecording,
    onToggleMute,
    onToggleVideo,
    onTogglePresent,
    onMenu,
    onLeave,
    canvas,
    placeholderLabel = 'Live video canvas',
  } = props;

  const [tab, setTab] = useState<LiveTab>('details');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1500);
    return () => window.clearTimeout(t);
  }, [copied]);

  const peopleInMeeting: InterviewLiveAttendee[] = useMemo(() => {
    const list: InterviewLiveAttendee[] = [...companyAttendees];
    if (candidate) {
      list.push({
        id: `cand-${candidate.id}`,
        name: candidate.full_name,
        email: candidate.email ?? null,
        role: 'candidate',
      });
    }
    return list;
  }, [companyAttendees, candidate]);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(meetingUrl);
      setCopied(true);
    } catch {
      /* ignore */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex flex-col bg-[color:var(--vscode-bg-primary,#0b0b0f)] text-[color:var(--vscode-text-primary,#F9FAFB)]"
      role="region"
      aria-label="Live interview"
    >
      <div className="flex min-h-0 flex-1 overflow-hidden">
        {/* Video canvas */}
        <div className="relative flex min-h-0 flex-1 flex-col bg-[#0b0b0f]">
          <div className="absolute left-4 top-4 z-10 flex items-center gap-2">
            <span
              className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[11px] font-medium text-white/90 backdrop-blur"
            >
              <span
                className={`h-1.5 w-1.5 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-white/50'}`}
                aria-hidden
              />
              {recording ? 'Recording' : 'Not recording'}
            </span>
            <span className="rounded-full border border-white/10 bg-black/40 px-2.5 py-1 text-[11px] font-medium text-white/90 backdrop-blur">
              {meetingTitle}
            </span>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden">
            {canvas ? (
              <div className="absolute inset-0">{canvas}</div>
            ) : (
              <div className="flex flex-col items-center gap-3 text-white/60">
                <div
                  className="flex h-20 w-20 items-center justify-center rounded-full border border-white/10 bg-white/5"
                  aria-hidden
                >
                  <Video className="h-8 w-8" />
                </div>
                <p className="text-[13px] font-medium">{placeholderLabel}</p>
                <p className="max-w-[280px] text-center text-[11px] leading-relaxed text-white/50">
                  Live streaming is not wired in this build. Recording, transcription,
                  and media controls below are visual placeholders.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right rail */}
        <aside
          className="flex h-full w-[320px] shrink-0 flex-col border-l bg-[var(--vscode-bg-elevated,#111827)] text-[color:var(--vscode-text-primary,#F9FAFB)]"
          style={{ borderColor: 'rgba(255,255,255,0.08)' }}
          aria-label="Live interview rail"
        >
          <nav
            className="flex shrink-0 items-center gap-1 border-b px-2 pt-2"
            style={{ borderColor: 'rgba(255,255,255,0.08)' }}
            role="tablist"
          >
            {(['details', 'candidate'] as const).map((t) => {
              const active = t === tab;
              const label = t === 'details' ? 'Details' : 'Candidate';
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => setTab(t)}
                  className={`relative h-9 px-3 text-[12px] font-medium transition-colors ${
                    active ? 'text-white' : 'text-white/60 hover:text-white/85'
                  }`}
                >
                  {label}
                  {active ? (
                    <span
                      className="absolute inset-x-2 bottom-0 h-[2px] rounded-t-full"
                      style={{ background: 'var(--ggj-accent,#10B981)' }}
                    />
                  ) : null}
                </button>
              );
            })}
          </nav>

          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 text-[12px] leading-relaxed">
            {tab === 'details' ? (
              <div className="space-y-4">
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    Meeting
                  </p>
                  <p className="truncate text-[13px] font-medium text-white">{meetingTitle}</p>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    Meeting URL
                  </p>
                  <div className="flex items-center gap-2 rounded-[10px] border border-white/10 bg-white/5 px-2.5 py-1.5">
                    <span className="min-w-0 flex-1 truncate text-[12px] text-white/90" title={meetingUrl}>
                      {meetingUrl.replace(/^https?:\/\//, '')}
                    </span>
                    <button
                      type="button"
                      onClick={() => void copyUrl()}
                      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-[6px] text-white/70 hover:bg-white/10 hover:text-white"
                      aria-label={copied ? 'Copied' : 'Copy meeting URL'}
                      title={copied ? 'Copied' : 'Copy meeting URL'}
                    >
                      <Copy className="h-3 w-3" aria-hidden />
                    </button>
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    Company attendees
                  </p>
                  <p className="text-[12px] text-white/85">{companyName}</p>
                  <ul className="mt-1.5 space-y-1">
                    {companyAttendees.length === 0 ? (
                      <li className="text-[11px] text-white/50 italic">No panelists added.</li>
                    ) : (
                      companyAttendees.map((a) => (
                        <li key={a.id} className="flex items-center gap-2 text-[12px] text-white/80">
                          <span
                            className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                            style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
                            aria-hidden
                          >
                            {initials(a.name)}
                          </span>
                          <span className="min-w-0 flex-1 truncate">{a.name}</span>
                        </li>
                      ))
                    )}
                  </ul>
                </div>
                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    Candidate
                  </p>
                  {candidate ? (
                    <div className="flex items-center gap-2">
                      <span
                        className="flex h-6 w-6 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                        style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
                        aria-hidden
                      >
                        {initials(candidate.full_name)}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate text-[12px] text-white/90">{candidate.full_name}</p>
                        {candidate.email ? (
                          <p className="truncate text-[11px] text-white/50">{candidate.email}</p>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p className="text-[11px] text-white/50 italic">No candidate linked.</p>
                  )}
                </div>
                <div>
                  <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                    In this meeting ({peopleInMeeting.length})
                  </p>
                  <ul className="space-y-1.5">
                    {peopleInMeeting.map((p) => (
                      <li
                        key={p.id}
                        className="flex items-center gap-2 rounded-[8px] border border-white/10 bg-white/5 px-2 py-1.5"
                      >
                        <span
                          className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-semibold uppercase"
                          style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
                          aria-hidden
                        >
                          {initials(p.name)}
                        </span>
                        <span className="min-w-0 flex-1 truncate text-[12px] text-white/85">{p.name}</span>
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center text-white/60"
                          aria-hidden
                          title={p.muted ? 'Muted' : 'Live mic'}
                        >
                          {p.muted ? <MicOff className="h-3 w-3" /> : <Mic className="h-3 w-3" />}
                        </span>
                        <span
                          className="inline-flex h-5 w-5 items-center justify-center text-white/60"
                          aria-hidden
                          title={p.videoOn === false ? 'Video off' : 'Video on'}
                        >
                          {p.videoOn === false ? <VideoOff className="h-3 w-3" /> : <Video className="h-3 w-3" />}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <LiveCandidateTab candidate={candidate} />
            )}
          </div>
        </aside>
      </div>

      {/* Bottom action strip */}
      <div
        className="flex h-14 shrink-0 items-center justify-between border-t px-4"
        style={{
          borderColor: 'rgba(255,255,255,0.08)',
          background: 'var(--vscode-bg-elevated,#0F1115)',
        }}
      >
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-[12px] font-medium tabular-nums text-white/90">
            <span
              className={`h-1.5 w-1.5 rounded-full ${recording ? 'bg-red-500 animate-pulse' : 'bg-white/50'}`}
              aria-hidden
            />
            {formatElapsed(elapsedSeconds)}
          </span>
          <LiveActionButton
            icon={recording ? <Square className="h-3.5 w-3.5 fill-current" /> : <Circle className="h-3.5 w-3.5" />}
            label={recording ? 'Stop recording' : 'Start recording'}
            onClick={onToggleRecording}
          />
        </div>

        <div className="flex items-center gap-1.5">
          <LiveActionButton
            icon={muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
            label={muted ? 'Unmute' : 'Mute'}
            onClick={onToggleMute}
            tone={muted ? 'warning' : 'neutral'}
          />
          <LiveActionButton
            icon={videoOn ? <Video className="h-3.5 w-3.5" /> : <VideoOff className="h-3.5 w-3.5" />}
            label={videoOn ? 'Stop video' : 'Start video'}
            onClick={onToggleVideo}
          />
          <LiveActionButton
            icon={<MonitorUp className="h-3.5 w-3.5" />}
            label={presenting ? 'Stop presenting' : 'Present'}
            onClick={onTogglePresent}
            tone={presenting ? 'accent' : 'neutral'}
          />
          <LiveActionButton icon={<MoreHorizontal className="h-3.5 w-3.5" />} label="Menu" onClick={onMenu} />
        </div>

        <div className="flex items-center">
          <button
            type="button"
            onClick={onLeave}
            className="inline-flex h-9 items-center gap-1.5 rounded-[10px] px-3.5 text-[13px] font-medium text-white transition-opacity hover:opacity-90"
            style={{ background: '#DC2626' }}
          >
            <LogOut className="h-3.5 w-3.5" aria-hidden />
            Leave
          </button>
        </div>
      </div>
    </div>
  );
}

function LiveActionButton({
  icon,
  label,
  onClick,
  tone = 'neutral',
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  tone?: 'neutral' | 'accent' | 'warning';
}) {
  const toneClass =
    tone === 'accent'
      ? 'bg-[var(--ggj-accent,#10B981)] text-white border-transparent hover:opacity-90'
      : tone === 'warning'
        ? 'border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/15'
        : 'border-white/10 bg-white/5 text-white/85 hover:bg-white/10 hover:text-white';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-[10px] border px-3 text-[12px] font-medium transition-colors ${toneClass}`}
      aria-label={label}
      title={label}
    >
      <span aria-hidden>{icon}</span>
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function LiveCandidateTab({ candidate }: { candidate: Candidate | null }) {
  if (!candidate) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
        <UserIcon className="h-6 w-6 text-white/40" aria-hidden />
        <p className="text-[12px] text-white/60">No candidate linked.</p>
      </div>
    );
  }
  const skills = candidate.parsed_skills ?? [];
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[12px] font-semibold uppercase"
          style={{ background: 'var(--ggj-accent-soft,#D1FAE5)', color: '#065F46' }}
          aria-hidden
        >
          {initials(candidate.full_name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-semibold text-white">{candidate.full_name}</p>
          <p className="truncate text-[11px] text-white/60">
            {candidate.current_title ?? '—'}
            {candidate.current_company ? ` · ${candidate.current_company}` : ''}
          </p>
          {candidate.location ? (
            <p className="truncate text-[11px] text-white/50">{candidate.location}</p>
          ) : null}
        </div>
      </div>

      {candidate.linkedin_url ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">LinkedIn</p>
          <a
            href={candidate.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            className="truncate text-[12px] text-white/85 underline-offset-2 hover:underline"
          >
            {candidate.linkedin_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
          </a>
        </div>
      ) : null}

      {skills.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">Skills</p>
          <div className="flex flex-wrap gap-1.5">
            {skills.slice(0, 12).map((s) => (
              <span
                key={s}
                className="inline-flex h-6 items-center rounded-full border border-white/10 bg-white/5 px-2 text-[11px] text-white/80"
              >
                {s}
              </span>
            ))}
            {skills.length > 12 ? (
              <span className="inline-flex h-6 items-center rounded-full px-2 text-[11px] text-white/50">
                +{skills.length - 12} more
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {candidate.education && candidate.education.length > 0 ? (
        <div>
          <p className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-white/40">Education</p>
          <ul className="space-y-1.5">
            {candidate.education.slice(0, 3).map((e, i) => (
              <li key={`${e.institution}-${i}`}>
                <p className="text-[12px] text-white/90">{e.degree}</p>
                <p className="text-[11px] text-white/55">
                  {e.institution}
                  {e.year ? ` · ${e.year}` : ''}
                </p>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="pt-2">
        <div
          className="flex items-center gap-2 rounded-[10px] border border-white/10 bg-white/5 px-2 py-1.5"
          title="Notes and quick messaging — coming soon (TODO §8-v2)"
        >
          <MessageSquare className="h-3.5 w-3.5 text-white/60" aria-hidden />
          <span className="text-[11px] text-white/50">In-meeting chat · coming soon</span>
        </div>
      </div>
    </div>
  );
}
